import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'helpers/Analytics';
import { Link } from 'react-router-dom';
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  HelpBlock,
  Row,
} from 'react-bootstrap';
import Panel from 'components/Panel/Panel';
import styles from './CreateProject.module.scss';
import RolesSelector from 'components/Shared/Selectors/RolesSelector';
import { addCredits, createProject } from 'redux/modules/textProjects';
import useAuth from 'hooks/useAuth';
import MarkdownSection from 'components/TextProjects/MarkdownSection';
import ProjectMediaPanel from 'components/TextProjects/ProjectMediaPanel';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import { setSuccessMessage } from 'redux/modules/successMessage';
import Modal from 'components/Modal/Modal';

const CreateProject = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
  const [mediaPayload, setMediaPayload] = useState({
    file: null,
    thumbnail: null,
    crop: null,
    uploading: false,
  });

  const featuredRoles = (user?.featured_roles || []).slice(0, 5);

  const projectSelect = (state) => state.textProjects;
  const projectState = useSelector(projectSelect);

  const { creatingProject } = projectState;
  const busy = creatingProject || mediaPayload.uploading;

  const finishCreate = (response, { creditsFailed = false } = {}) => {
    trackEvent(
      mediaPayload.file
        ? 'freelancer-creates-media-project'
        : 'freelancer-creates-text-project'
    );
    dispatch(
      setSuccessMessage(
        creditsFailed
          ? 'Project created. Roles could not be saved — you can add them on the edit page.'
          : 'Your project has successfully been created'
      )
    );
    window.location.href = response.links.main_relative;
  };

  useEffect(() => {
    if (!user.email_verified) {
      setShowEmailVerifyModal(true);
    }
  }, []);

  const handleRolesChange = (roles) => {
    setSelectedRoles(roles);
  };

  const buildCreateData = () => {
    const data = {
      title,
      description,
    };

    if (mediaPayload.file) {
      data.file = mediaPayload.file;
    }

    if (mediaPayload.thumbnail) {
      data.thumbnail = mediaPayload.thumbnail;
      if (mediaPayload.crop) {
        data.crop = mediaPayload.crop;
      }
    }

    return data;
  };

  const handleCreateProject = () => {
    if (mediaPayload.uploading || creatingProject) {
      return;
    }

    if (!selectedRoles.length) {
      setErrors({
        roles: ['Please fill in this field with at least 1 role selected'],
      });
      return;
    }

    setErrors({});

    dispatch(createProject(buildCreateData()))
      .then((response) => {
        return dispatch(
          addCredits(
            response.id,
            user.id,
            selectedRoles.map((role) => role.value)
          )
        )
          .then(() => finishCreate(response))
          .catch((error) => {
            console.error('Error creating credits:', error);
            // Keep media payload — create already succeeded; redirect with warning
            finishCreate(response, { creditsFailed: true });
          });
      })
      .catch((error) => {
        console.error('Error creating project:', error);
        // Upload reference is still valid on create failure — keep for retry
        setErrors(error.errors || {});
      });
  };

  return (
    <div className="mt++ lato-regular">
      <Grid>
        <Row>
          <Col lg={8}>
            <Breadcrumbs className={styles.breadcrumbs}>
              <Link to="/projects/manage">Manage Projects</Link>
              Create Project
            </Breadcrumbs>
            <Panel className={styles.container}>
              <FormGroup controlId="project-title">
                <ControlLabel className="fs-15">Title</ControlLabel>
                <FormControl
                  type="text"
                  value={title}
                  placeholder="Enter the title of your project here"
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength="140"
                  style={{ fontSize: '14px' }}
                  disabled={busy}
                />
                {errors?.title && (
                  <HelpBlock className={styles.error}>
                    {errors?.title[0]}
                  </HelpBlock>
                )}
              </FormGroup>
              <ProjectMediaPanel
                mode="create"
                disabled={busy}
                onChange={setMediaPayload}
              />
              {errors?.file && (
                <HelpBlock className={styles.error}>{errors.file[0]}</HelpBlock>
              )}
              {errors?.project_id && (
                <HelpBlock className={styles.error}>
                  {errors.project_id[0]}
                </HelpBlock>
              )}
              <FormGroup controlId="project-description">
                <ControlLabel className="fs-15">Description</ControlLabel>
                <FormControl
                  componentClass="textarea"
                  value={description}
                  placeholder="Enter the description of your project here. Use the cheat sheet on this page to see how you can make use of the Markdown support and various services embeds"
                  onChange={(event) => setDescription(event.target.value)}
                  style={{ fontSize: '14px' }}
                  rows="10"
                  disabled={busy}
                />
                {errors?.description && (
                  <HelpBlock className={styles.error}>
                    {errors?.description[0]}
                  </HelpBlock>
                )}
              </FormGroup>
              <FormGroup controlId="project-credits">
                <ControlLabel className="fs-15">
                  Your project role(s)
                </ControlLabel>
                <RolesSelector
                  key={JSON.stringify(featuredRoles)}
                  maxValues={5}
                  style={{ width: '100%' }}
                  existing={featuredRoles}
                  onSelect={handleRolesChange}
                />
                {errors?.roles && (
                  <HelpBlock className={styles.error}>
                    {errors.roles[0]}
                  </HelpBlock>
                )}
              </FormGroup>
              <div className={styles.buttonContainer}>
                <Button
                  bsStyle="primary"
                  onClick={() => {
                    !user.email_verified
                      ? setShowEmailVerifyModal(true)
                      : handleCreateProject();
                  }}
                  disabled={busy}
                >
                  {mediaPayload.uploading
                    ? 'Uploading media…'
                    : creatingProject
                    ? 'Creating…'
                    : 'Create Project'}
                </Button>
              </div>
            </Panel>
          </Col>
          <Col lg={4}>
            <MarkdownSection createPage />
          </Col>
        </Row>
      </Grid>
      <Modal
        show={showEmailVerifyModal}
        onExit={() => setShowEmailVerifyModal(false)}
        closeButton
      >
        <div className="p++">
          <h3 className="lato-bold text-center mb">Activate your Email</h3>
          <p>
            Before you can add a new project, you need to activate your account.
          </p>
          <p>
            Please check your email for an email from us with an activation
            button and click it.
          </p>
          <p>
            Please check your spam/junk folder in case it has ended up in there.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CreateProject;
